import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { tshareApi } from '../services/api';
import { Lightbox } from '../components/Lightbox';

function errMsg(e, f) { return e?.response?.data?.error || f; }
function normalizeUrl(u) { return u && !/^https?:\/\//i.test(u) ? `https://${u}` : (u || ''); }
const WEEK = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
// 到店时间：8月22日 周五 12:00
function fmtVisit(d) {
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return '';
  return `${dt.getMonth() + 1}月${dt.getDate()}日 ${WEEK[dt.getDay()]} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}
// 把纯文本里的 http(s) 网址渲染成可点链接（小红书链接等）
function linkify(text) {
  if (!text) return text;
  return String(text).split(/(https?:\/\/[^\s]+)/g).map((p, i) =>
    /^https?:\/\//i.test(p)
      ? <a key={i} href={p} target="_blank" rel="noreferrer" style={{ color: '#FA1C51', textDecoration: 'underline', wordBreak: 'break-all' }} onClick={(e) => e.stopPropagation()}>{p}</a>
      : p
  );
}

const STATUS = {
  selected:        { t: '已选中 · 待探店', c: '#7c3aed' },
  visited:         { t: '已探店', c: '#0891b2' },
  draft_submitted: { t: '已出草稿', c: '#b45309' },
  draft_approved:  { t: '草稿已确认', c: '#15803d' },
  published:       { t: '已发布', c: '#FA1C51' },
  done:            { t: '已完成', c: '#15803d' },
};
const IN_PROGRESS = ['selected', 'visited', 'draft_submitted', 'draft_approved', 'published', 'done'];

// 可点开放大的图片墙
// 稿子里的视频（2026-10-01 起交稿能带视频，人工审稿时商家也要看得到）
function VideoWall({ videos }) {
  if (!videos || !videos.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
      {videos.map((u) => <video key={u} src={u} controls playsInline style={{ width: '100%', maxHeight: 420, borderRadius: 10, background: '#000' }} />)}
    </div>
  );
}

function ImgWall({ images, onOpen }) {
  if (!images || !images.length) return null;
  return (
    <div style={s.imgGrid}>
      {images.map((u, i) => <img key={i} src={u} alt="" style={s.img} onClick={() => onOpen(images, i)} />)}
    </div>
  );
}

function Avatar({ src, name }) {
  if (src) return <img src={src} alt="" style={s.avatar} />;
  return <div style={s.avatarFallback}>{(name || '博')[0]}</div>;
}

export default function MerchantShare() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const view = searchParams.get('view');   // select | drafts | report | null(总链接)
  const appId = searchParams.get('app');    // 聚焦单个博主
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [saving, setSaving] = useState(false);
  const [approvingId, setApprovingId] = useState('');
  const [msg, setMsg] = useState('');
  const [lightbox, setLightbox] = useState(null);   // { images, index }
  const openImg = (images, index) => setLightbox({ images, index });

  const load = () => tshareApi.get(token).then(setData).catch((e) => setFatal(errMsg(e, '链接无效或已失效'))).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  if (loading) return <div style={s.center}><img src="/favicon.png" alt="" style={{ width: 64, height: 64, borderRadius: 16 }} /></div>;
  if (fatal) return <div style={s.center}><p style={s.fatal}>{fatal}</p></div>;

  const { activity } = data;
  const applicants = appId ? data.applicants.filter((a) => String(a._id) === String(appId)) : data.applicants;
  const toPick = applicants.filter((a) => a.status === 'listed');
  const chosen = applicants.filter((a) => IN_PROGRESS.includes(a.status));
  const draftList = applicants.filter((a) => ['draft_submitted', 'draft_approved', 'published', 'done'].includes(a.status)
    && a.draft && (a.draft.content || (a.draft.images || []).length));

  // 有 view 时只显示对应板块（含空态，便于运营预览）；无 view（总链接）显示有数据的板块
  const showSelect  = view === 'select'  || (!view && toPick.length > 0);
  const showDrafts  = view === 'drafts'  || (!view && applicants.some((a) => a.status === 'draft_submitted'));
  const showReport  = view === 'report'  || (!view && chosen.length > 0);

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const submit = async () => {
    setSaving(true); setMsg('');
    try { await tshareApi.select(token, Array.from(picked)); setPicked(new Set()); await load(); setMsg('已提交，感谢您的选择'); }
    catch (e) { setMsg(errMsg(e, '提交失败')); }
    finally { setSaving(false); }
  };
  const approve = async (id) => {
    setApprovingId(id); setMsg('');
    try { await tshareApi.approveDraft(token, id); await load(); }
    catch (e) { setMsg(errMsg(e, '确认失败')); }
    finally { setApprovingId(''); }
  };
  const reject = async (id, comment) => {
    setApprovingId(id); setMsg('');
    try { await tshareApi.rejectDraft(token, id, comment); await load(); setMsg('已退回博主修改'); }
    catch (e) { setMsg(errMsg(e, '退回失败')); }
    finally { setApprovingId(''); }
  };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.hTitle}>{activity.merchantName}</div>
        <div style={s.hSub}>{activity.name} · 博主探店</div>
      </div>

      <div style={s.body}>
        {activity.intro && view !== 'drafts' && (
          <div style={s.introCard}>
            <div style={s.introLabel}>活动说明</div>
            <div style={s.introText}>{activity.intro}</div>
          </div>
        )}

        {showSelect && (
          <div style={s.card}>
            <div style={s.cardTitle}>请挑选博主{activity.neededCount ? `（建议 ${activity.neededCount} 位）` : ''}</div>
            {toPick.length === 0 ? <div style={s.dim}>暂无待挑选的博主。</div> : (<>
              <div style={s.dim}>以下是为您初选的博主，勾选您希望到店的博主后提交。</div>
              <div style={s.pickList}>
                {toPick.map((a) => (
                  <label key={a._id} style={{ ...s.pickRow, ...(picked.has(a._id) ? s.pickOn : {}) }}>
                    <input type="checkbox" checked={picked.has(a._id)} onChange={() => toggle(a._id)} style={s.checkbox} />
                    <Avatar src={a.avatar} name={a.bloggerName} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={s.pName}>{a.bloggerName || '博主'} <span style={s.pFans}>{a.xhsFollowers || 0} 粉</span></div>
                      <div style={s.pSub}>小红书：{a.xhsAccount || '—'}</div>
                      {(a.visitAt || a.visitTimeRequested) && (
                        <div style={s.reqTime}>到店时间：{a.visitAt ? fmtVisit(a.visitAt) : linkify(a.visitTimeRequested)}{a.visitPeopleCount > 1 ? ` · ${a.visitPeopleCount}人` : ''}</div>
                      )}
                      {a.bio && <div style={s.pBio}>{linkify(a.bio)}</div>}
                      {a.xhsUrl && <a style={s.pLink} href={normalizeUrl(a.xhsUrl)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>查看主页 ›</a>}
                    </div>
                  </label>
                ))}
              </div>
              {msg && <div style={s.msg}>{msg}</div>}
              <button style={{ ...s.primaryBtn, ...(saving || picked.size === 0 ? s.disabled : {}) }} onClick={submit} disabled={saving || picked.size === 0}>
                提交选择{picked.size ? `（${picked.size} 位）` : ''}
              </button>
            </>)}
          </div>
        )}

        {showDrafts && (
          <div style={s.card}>
            <div style={s.cardTitle}>确认草稿</div>
            {draftList.length === 0 ? <div style={s.dim}>暂无待确认的草稿。</div> : (<>
              <div style={s.dim}>请查看博主提交的内容草稿，确认无误后点「通过」；如需修改请联系运营。</div>
              {draftList.map((a) => (
                <DraftCard key={a._id} a={a} busy={approvingId === a._id} onOpenImg={openImg}
                  onApprove={() => approve(a._id)} onReject={(c) => reject(a._id, c)} />
              ))}
              {msg && <div style={s.msg}>{msg}</div>}
            </>)}
          </div>
        )}

        {showReport && (
          <div style={s.card}>
            <div style={s.cardTitle}>{view === 'report' ? '探店报告' : '探店进度'}{chosen.length ? `（${chosen.length} 位）` : ''}</div>
            {chosen.length === 0 ? <div style={s.dim}>暂无已选中的博主。</div> : chosen.map((a) => <ChosenCard key={a._id} a={a} onOpenImg={openImg} />)}
          </div>
        )}
        <div style={{ height: 24 }} />
      </div>
      {lightbox && (
        <Lightbox images={lightbox.images} index={lightbox.index}
          onIndex={(i) => setLightbox((p) => ({ ...p, index: i }))} onClose={() => setLightbox(null)} />
      )}
    </div>
  );
}

// 确认草稿卡：只给商家看内容本身（不显示活动说明 / 备注）
function DraftCard({ a, busy, onApprove, onReject, onOpenImg }) {
  const [comment, setComment] = useState('');
  const st = STATUS[a.status] || STATUS.selected;
  return (
    <div style={s.chosen}>
      <div style={s.chTop}>
        <Avatar src={a.avatar} name={a.bloggerName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.pName}>{a.bloggerName || '博主'} <span style={s.pFans}>{a.xhsFollowers || 0} 粉</span></div>
          <div style={s.pSub}>小红书：{a.xhsAccount || '—'}</div>
        </div>
        <span style={{ ...s.chBadge, color: st.c }}>{st.t}</span>
      </div>
      <div style={s.detail}>
        {a.draft?.title && <div style={s.dTitle}>{a.draft.title}</div>}
        {a.draft?.content && <div style={s.dContent}>{linkify(a.draft.content)}</div>}
        <ImgWall images={a.draft?.images} onOpen={onOpenImg} />
        <VideoWall videos={a.draft?.videos} />
      </div>
      {a.status === 'draft_submitted' ? (<>
        <textarea style={s.commentBox} placeholder="如需修改，简单说明即可（复杂意见可在微信联系我们）" value={comment} onChange={(e) => setComment(e.target.value)} />
        <div style={s.draftBtnRow}>
          <button style={{ ...s.approveBtn, flex: 1, marginTop: 0, ...(busy ? s.disabled : {}) }} onClick={onApprove} disabled={busy}>{busy ? '处理中…' : '通过'}</button>
          <button style={{ ...s.rejectBtn, ...(busy ? s.disabled : {}) }} onClick={() => onReject(comment)} disabled={busy}>退回修改</button>
        </div>
      </>) : <div style={s.approvedTag}>已确认</div>}
    </div>
  );
}

function ChosenCard({ a, onOpenImg }) {
  const [open, setOpen] = useState(false);
  const st = STATUS[a.status] || STATUS.selected;
  const hasDraft = a.draft && (a.draft.content || (a.draft.images || []).length);
  return (
    <div style={s.chosen}>
      <div style={s.chTop}>
        <Avatar src={a.avatar} name={a.bloggerName} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.pName}>{a.bloggerName || '博主'} <span style={s.pFans}>{a.xhsFollowers || 0} 粉</span></div>
          <div style={s.pSub}>小红书：{a.xhsAccount || '—'}{a.visitAt ? ` · 探店 ${fmtVisit(a.visitAt)}` : (a.visitTimeConfirmed ? ` · 探店 ${a.visitTimeConfirmed}` : '')}</div>
        </div>
        <span style={{ ...s.chBadge, color: st.c }}>{st.t}</span>
      </div>
      {(a.preferredName || a.contactPhone) && (
        <div style={s.contact}>
          {a.preferredName && <span>称呼：{a.preferredName}</span>}
          {a.contactPhone && <a style={s.contactTel} href={`tel:${a.contactPhone}`}>电话：{a.contactPhone}</a>}
        </div>
      )}
      {a.bio && <div style={s.pBio}>{linkify(a.bio)}</div>}
      {(hasDraft || a.xhsPostUrl) && (
        <button style={s.viewBtn} onClick={() => setOpen((v) => !v)}>{open ? '收起' : (a.xhsPostUrl ? '查看发帖' : '查看草稿')}</button>
      )}
      {open && (
        <div style={s.detail}>
          {a.draft?.title && <div style={s.dTitle}>{a.draft.title}</div>}
          {a.draft?.content && <div style={s.dContent}>{linkify(a.draft.content)}</div>}
          <ImgWall images={a.draft?.images} onOpen={onOpenImg} />
          <VideoWall videos={a.draft?.videos} />
          {a.xhsPostUrl && <a style={s.link} href={normalizeUrl(a.xhsPostUrl)} target="_blank" rel="noreferrer">打开小红书发帖 ›</a>}
          <ImgWall images={a.proofImages} onOpen={onOpenImg} />
        </div>
      )}
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: '#f7f7f8', maxWidth: 520, margin: '0 auto' },
  center: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', padding: 32 },
  fatal: { fontSize: 15, color: '#888', textAlign: 'center' },
  header: { background: '#FA1C51', color: '#fff', padding: '22px 20px 20px' },
  hTitle: { fontSize: 20, fontWeight: 800 },
  hSub: { fontSize: 13, opacity: 0.9, marginTop: 4 },
  body: { padding: '12px' },
  introCard: { background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  introLabel: { fontSize: 12, color: '#9ca3af', fontWeight: 600, marginBottom: 6 },
  introText: { fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap' },
  card: { background: '#fff', borderRadius: 14, padding: 16, marginBottom: 12, boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 6 },
  dim: { fontSize: 12, color: '#9ca3af', marginBottom: 10, lineHeight: 1.6 },
  pickList: { display: 'flex', flexDirection: 'column', gap: 8 },
  pickRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px', border: '1px solid #eee', borderRadius: 10, cursor: 'pointer' },
  pickOn: { borderColor: '#FA1C51', background: '#fff7f8' },
  checkbox: { marginTop: 14 },
  avatar: { width: 44, height: 44, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 },
  avatarFallback: { width: 44, height: 44, borderRadius: '50%', background: '#FA1C51', color: '#fff', fontSize: 18, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  pName: { fontSize: 15, fontWeight: 700, color: '#111' },
  pFans: { fontSize: 12, fontWeight: 400, color: '#FA1C51' },
  pSub: { fontSize: 12, color: '#9ca3af', marginTop: 3 },
  pBio: { fontSize: 13, color: '#555', lineHeight: 1.6, marginTop: 6, whiteSpace: 'pre-wrap' },
  pLink: { display: 'inline-block', fontSize: 12, color: '#FA1C51', fontWeight: 600, textDecoration: 'none', marginTop: 6 },
  msg: { fontSize: 13, color: '#15803d', margin: '10px 0 0' },
  primaryBtn: { width: '100%', marginTop: 14, padding: '14px', background: '#FA1C51', color: '#fff', border: 'none', borderRadius: 12, fontSize: 16, fontWeight: 700 },
  disabled: { opacity: 0.5 },
  chosen: { padding: '12px 0', borderBottom: '1px solid #f5f5f5' },
  chTop: { display: 'flex', alignItems: 'flex-start', gap: 10 },
  chBadge: { fontSize: 12, fontWeight: 700, flexShrink: 0, marginTop: 2 },
  viewBtn: { marginTop: 10, padding: '6px 14px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, cursor: 'pointer' },
  detail: { marginTop: 10, background: '#fafafa', borderRadius: 10, padding: 12 },
  dTitle: { fontSize: 15, fontWeight: 700, color: '#111', marginBottom: 6 },
  dContent: { fontSize: 14, color: '#374151', lineHeight: 1.7, whiteSpace: 'pre-wrap', marginBottom: 10 },
  imgGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  img: { width: 92, height: 92, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in' },
  link: { display: 'inline-block', fontSize: 14, color: '#FA1C51', fontWeight: 700, textDecoration: 'none', margin: '4px 0 10px' },
  approveBtn: { marginTop: 10, width: '100%', padding: '12px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  approvedTag: { marginTop: 10, fontSize: 13, color: '#15803d', fontWeight: 600 },
  reqTime: { display: 'inline-block', fontSize: 14, fontWeight: 700, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '3px 10px', margin: '8px 0 2px' },
  contact: { display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: 14, color: '#111', fontWeight: 600, marginTop: 8 },
  contactTel: { color: '#FA1C51', textDecoration: 'none' },
  commentBox: { width: '100%', boxSizing: 'border-box', padding: '10px 12px', border: '1px solid #eee', borderRadius: 10, minHeight: 60, resize: 'vertical', marginTop: 10, fontSize: 14, outline: 'none', lineHeight: 1.6 },
  draftBtnRow: { display: 'flex', gap: 10, marginTop: 10 },
  rejectBtn: { padding: '12px 16px', background: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
};
